/** /canned_notifications/ + /canned_notification_groups/ — the notification pack's
 *  archetype catalog, and materialising it into a tenant.
 *
 *  Read-only catalog: these rows are materialised from code by
 *  `manage.py pack_seed`, so the console shows what is on offer and never edits
 *  the recipe. The one write is `materialise`, which builds a tenant's
 *  notifications — switched OFF — or refreshes ones already built.
 *
 *  Hand-authored wrapper (console convention: never depend on generated types
 *  for feature modules).
 */
import { apiGet, apiPost } from './client';
import type { PaginatedResponse } from './users';

/** The four independently-overridable aspects of a materialised notification.
 *
 *  While an aspect is managed the write funnel REFUSES edits to it and a
 *  refresh reapplies the canned definition. Turning it off hands that aspect to
 *  the tenant for good — every later refresh skips it.
 */
export interface ManagedAspects {
  /** The notification's rules and rules-match type. */
  rules: boolean;
  /** Which blocks and block lists it composes. */
  refs: boolean;
  /** The per-channel subjects and bodies. */
  content: boolean;
  /** Timing (event, time delta) and the delivery-method set. */
  delivery: boolean;
}

export type ManagedAspectKey = keyof ManagedAspects;

/** `managed_*` column name for each aspect — what the "unmanage" call flips. */
export const MANAGED_FLAG: Record<ManagedAspectKey, string> = {
  rules: 'managed_rules',
  refs: 'managed_refs',
  content: 'managed_content',
  delivery: 'managed_delivery',
};

/** Human labels, in the order the editor shows them. */
export const MANAGED_LABELS: { key: ManagedAspectKey; label: string; help: string }[] = [
  { key: 'delivery', label: 'Delivery', help: 'When it fires and which channels it uses.' },
  { key: 'content', label: 'Content', help: 'The subject and body of every channel.' },
  { key: 'refs', label: 'Blocks', help: 'Which blocks and block lists it composes.' },
  { key: 'rules', label: 'Rules', help: 'The conditions that decide whether it sends.' },
];

export interface CannedNotificationListItem {
  id: number;
  codename: string;
  name: string;
  description: string;
  event: string;
  order: number;
  enabled: boolean;
  pack_version: string;
  group: number;
  group_codename: string;
  group_name: string;
  /** Delivery channels this archetype builds, in spec order. */
  channels: string[];
  /** System block lists the materialised notification REFERENCES. */
  block_lists: string[];
  /** System rulesets it gates on. */
  rulesets: string[];
  /** How many tenants have it materialised. */
  materialised_count: number | null;
  created_ts: string | null;
  updated_ts: string | null;
}

/** One tenant that has this archetype materialised. */
export interface CannedTenantBinding {
  tour_operator_id: number;
  tour_operator_code: string;
  notification_id: number;
  notification_enabled: boolean;
  managed: ManagedAspects;
  /** True when this tenant is running an OLDER version of the recipe. Nothing
   *  else surfaces it — the notification looks entirely normal. */
  stale: boolean;
  materialised_ts: string | null;
  refreshed_ts: string | null;
}

export interface CannedNotificationDetail extends CannedNotificationListItem {
  /** The full recipe. Read-only — its source of truth is the backend repo. */
  definition: Record<string, unknown>;
  tenants: CannedTenantBinding[];
}

export interface CannedGroup {
  id: number;
  codename: string;
  name: string;
  description: string;
  order: number;
  enabled: boolean;
  pack_version: string;
  notifications: CannedNotificationListItem[];
  created_ts: string | null;
  updated_ts: string | null;
}

export interface MaterialiseResultRow {
  codename: string;
  status: 'created' | 'refreshed' | 'unchanged' | 'skipped';
  notification_id: number | null;
  /** What the refresh actually wrote. Empty on `unchanged`. */
  changed: string[];
  reason?: string;
}

export interface MaterialiseResult {
  tour_operator: number;
  dry_run: boolean;
  results: MaterialiseResultRow[];
}

/** 400 body when a write would change a pack-managed aspect. */
export interface ManagedRefusal {
  detail: string;
  managed: { aspect: ManagedAspectKey; flag: string; field: string }[];
}

/** Narrow an unknown error body to a managed-aspect refusal. */
export function asManagedRefusal(body: unknown): ManagedRefusal | null {
  if (!body || typeof body !== 'object') return null;
  const managed = (body as { managed?: unknown }).managed;
  if (!Array.isArray(managed) || managed.length === 0) return null;
  return body as ManagedRefusal;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** Group id or codename. */
  group?: string | number;
  event?: string;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.group !== undefined) usp.set('group', String(params.group));
  if (params.event) usp.set('event', params.event);
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(
  params: ListParams = {},
): Promise<PaginatedResponse<CannedNotificationListItem>> {
  return apiGet<PaginatedResponse<CannedNotificationListItem>>(
    `/canned_notifications/${toQuery(params)}`,
  );
}

export function retrieve(id: number): Promise<CannedNotificationDetail> {
  return apiGet<CannedNotificationDetail>(`/canned_notifications/${id}/`);
}

export function listGroups(): Promise<PaginatedResponse<CannedGroup>> {
  return apiGet<PaginatedResponse<CannedGroup>>('/canned_notification_groups/');
}

export function materialise(
  id: number,
  body: { tour_operator: number; dry_run?: boolean },
): Promise<MaterialiseResult> {
  return apiPost<MaterialiseResult>(`/canned_notifications/${id}/materialise/`, body);
}

export function materialiseGroup(
  id: number,
  body: { tour_operator: number; codenames?: string[]; dry_run?: boolean },
): Promise<MaterialiseResult> {
  return apiPost<MaterialiseResult>(`/canned_notification_groups/${id}/materialise/`, body);
}
